import { useEffect, useState, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { LogOut, RefreshCw, ShieldAlert } from 'lucide-react';
import { getAccessToken, clearTokens, isAccessTokenOversized } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { useBoundStore } from '@/stores';
import { authService } from '../services/auth.service';
import { authKeys } from '../hooks';
import { getHighestAuthRole, normalizeServerPermissions } from '../utils/auth-session.mapper';
import { getSessionVerificationStatus } from './auth-provider.utils';

interface AuthProviderProps {
  children: ReactNode;
}

interface SessionAccessError {
  correlationId?: string;
}

function getSessionCorrelationId(error: unknown): string | undefined {
  const correlationId = (
    error as {
      response?: { data?: { correlationId?: unknown } };
    }
  )?.response?.data?.correlationId;
  return typeof correlationId === 'string' && correlationId.trim() ? correlationId : undefined;
}

/**
 * AuthProvider handles session recovery on app load.
 * It checks for existing tokens and fetches the user profile to hydrate the store.
 *
 * IMPORTANT: This provider prevents race conditions between Zustand's persist
 * rehydration and actual token verification by:
 * 1. Clearing stale persisted data immediately on mount
 * 2. Fetching fresh profile from backend
 * 3. Only showing content after verification completes
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitializing, setIsInitializing] = useState(true);
  const [sessionAccessError, setSessionAccessError] = useState<SessionAccessError | null>(null);
  const initStarted = useRef(false);
  const queryClient = useQueryClient();
  // R30-025: redirect drives from AuthProvider (not the api-client interceptor)
  // so the loading-state transition happens AFTER the redirect is scheduled.
  // Previously a stale token + 401 refresh left the spinner up indefinitely
  // because the interceptor's `window.location.href = '/login'` raced with
  // `setIsInitializing(false)` here — React re-rendered into ProtectedRoute's
  // own spinner before the hard navigation landed, leaving the user "stuck."
  //
  // Note: AuthProvider mounts OUTSIDE RouterProvider (see App.tsx:19-21), so
  // we can't use TanStack Router's `useNavigate()` here — the router context
  // doesn't exist yet at first render. Use `window.location.href` instead;
  // a full reload is the correct semantic anyway (clears all in-memory state
  // before the user re-authenticates).

  const setUser = useBoundStore((state) => state.setUser);
  const setTenant = useBoundStore((state) => state.setTenant);
  const setLoading = useBoundStore((state) => state.setLoading);

  // R30-027 — When the api-client redirects to /login?reason=session_compromised
  // (because the server returned REFRESH_REUSED), show a distinct toast so the
  // user understands their session ended for SECURITY reasons (refresh token
  // replay), not just routine expiry. Fire once on first mount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'session_compromised') {
      toast.warning('Session ended for security', {
        description:
          'Your session was terminated because the refresh token was replayed. Please log in again.',
        duration: 8000,
      });
      // Strip the query param so a manual refresh of /login doesn't re-fire
      // the toast.
      params.delete('reason');
      const next = params.toString();
      const url = `${window.location.pathname}${next ? `?${next}` : ''}`;
      window.history.replaceState({}, '', url);
    } else if (params.get('reason') === 'session_invalid') {
      toast.info('Session reset', {
        description: 'Your cached session was invalid. Please log in again.',
      });
      params.delete('reason');
      const next = params.toString();
      const url = `${window.location.pathname}${next ? `?${next}` : ''}`;
      window.history.replaceState({}, '', url);
    }
  }, []);

  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (initStarted.current) return;
    initStarted.current = true;

    async function initializeAuth() {
      const token = getAccessToken();

      if (!token) {
        // No token - clear any stale persisted data and finish
        setUser(null);
        setTenant(null);
        setIsInitializing(false);
        return;
      }

      if (isAccessTokenOversized(token)) {
        clearTokens();
        localStorage.removeItem('tenant_id');
        setUser(null);
        setTenant(null);
        setIsInitializing(false);
        return;
      }

      // Don't clear user here — keep the Zustand-rehydrated user visible while we verify.
      // If the profile fetch succeeds, we replace with fresh data. If it fails due to
      // auth error (401/403), we clear. If it fails due to network error, the cached
      // user remains so the app stays usable.
      setLoading(true);

      try {
        // Fetch fresh user profile from backend
        // Response now includes roles[], permissions[], fullName, tenantName, tenantSlug
        const profile = await authService.getProfile();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const p = profile as Record<string, any>;
        const roles: string[] = p['roles'] || [];
        const permissions: string[] = p['permissions'] || [];

        // Normalize to match the User interface expected by the store
        const enrichedUser = {
          id: p['id'] as string,
          email: p['email'] as string,
          firstName: p['firstName'] as string,
          lastName: p['lastName'] as string,
          fullName:
            (p['fullName'] as string) || `${p['firstName'] || ''} ${p['lastName'] || ''}`.trim(),
          role: getHighestAuthRole(roles),
          permissions: normalizeServerPermissions(permissions),
          tenantId: p['tenantId'] as string | undefined,
          tenantName: p['tenantName'] as string | undefined,
          tenantSlug: p['tenantSlug'] as string | undefined,
          employeeId: p['employeeId'] as string | undefined,
          departmentId: p['departmentId'] as string | undefined,
          departmentName: p['departmentName'] as string | undefined,
          departmentCode: p['departmentCode'] as string | undefined,
          positionId: p['positionId'] as string | undefined,
          positionTitle: p['positionTitle'] as string | undefined,
          positionCode: p['positionCode'] as string | undefined,
          isChiefPosition: p['isChiefPosition'] as boolean | undefined,
          teamIds: (p['teamIds'] as string[] | undefined) || [],
          managedTeamIds: (p['managedTeamIds'] as string[] | undefined) || [],
          managedDepartmentId: p['managedDepartmentId'] as string | undefined,
          isActive: (p['isActive'] as boolean) ?? true,
          createdAt: (p['createdAt'] as string) || new Date().toISOString(),
          updatedAt: (p['updatedAt'] as string) || new Date().toISOString(),
        };

        // Hydrate the store with verified, enriched data
        setUser(enrichedUser);

        // Construct tenant info from profile
        if (enrichedUser.tenantId) {
          setTenant({
            id: enrichedUser.tenantId,
            name: enrichedUser.tenantName || 'Unknown Organization',
            code: enrichedUser.tenantSlug || enrichedUser.tenantId,
            slug: enrichedUser.tenantSlug,
            timezone: p['tenantTimezone'] as string | undefined,
            locale: p['tenantLocale'] as string | undefined,
            currency: p['tenantCurrency'] as string | undefined,
            dateFormat: p['tenantDateFormat'] as string | undefined,
            timeFormat: p['tenantTimeFormat'] as string | undefined,
          });
        } else {
          setTenant(null);
        }

        // Cache profile in React Query for other components
        queryClient.setQueryData(authKeys.profile(), enrichedUser);
      } catch (error) {
        // Check if it's an authentication error
        const status = getSessionVerificationStatus(error);

        if (status === 401 || status === 431) {
          // R30-025: token is invalid or expired — clear state AND drive
          // the redirect from here, not from the api-client interceptor.
          // Previously the interceptor's `window.location.href = '/login'`
          // raced with the `setIsInitializing(false)` below, leaving the
          // spinner up indefinitely. Now we navigate before the finally
          // dismisses the spinner so the user sees an immediate transition.
          console.warn('Session verification failed:', error);
          clearTokens();
          localStorage.removeItem('tenant_id');
          setUser(null);
          setTenant(null);
          // Skip toast + navigate when already on /login — avoids a toast
          // burst if a tampered token persists across reloads of the login
          // page, and avoids a no-op route transition.
          if (window.location.pathname !== '/login') {
            toast.info('Session expired', {
              description: 'Please log in again.',
            });
            // Hard navigation BEFORE the finally clears isInitializing.
            // The browser starts loading /login immediately; any React
            // re-render that follows is moot because the page is unloading.
            // This avoids the soft-lock where the api-client interceptor's
            // redirect raced with ProtectedRoute's own spinner.
            window.location.href = '/login';
          }
        } else if (status === 403) {
          // A valid identity can receive 403 when its profile permission
          // bootstrap is incomplete. Keep the tokens, but do not render the
          // application with stale cached permissions. Give the user an
          // explicit recovery path and preserve the correlation id for support.
          console.warn('Session profile access is not configured:', error);
          setSessionAccessError({
            correlationId: getSessionCorrelationId(error),
          });
        } else {
          // Network or server error — keep tokens AND keep Zustand rehydrated user state.
          // We intentionally do NOT call clearTokens() or setUser(null) here.
          // The user's session is likely still valid (the backend is just temporarily
          // unreachable). The Zustand-persisted user/tenant will remain in the store,
          // so the user stays logged in with cached state. When the backend comes back,
          // the next API call will work normally via the existing token refresh flow.
          console.warn(
            'Profile fetch failed (network/server error) — keeping cached session:',
            error,
          );
        }
      } finally {
        setLoading(false);
        setIsInitializing(false);
      }
    }

    initializeAuth();
  }, [setUser, setTenant, setLoading, queryClient]);

  // Show loading spinner during initialization - prevents any flash of stale content
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-sm text-muted-foreground animate-pulse">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (sessionAccessError) {
    return (
      <main className="min-h-screen bg-background px-4 py-12 sm:px-6">
        <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center text-center">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-md bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <ShieldAlert className="h-6 w-6" aria-hidden="true" />
          </div>
          <h1 className="text-xl font-semibold">
            Your session is active, but access is incomplete
          </h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            UniTree could not load your current permissions. Retry after an administrator updates
            your access, or sign out to use another account.
          </p>
          {sessionAccessError.correlationId && (
            <p className="mt-3 font-mono text-xs text-muted-foreground">
              Reference: {sessionAccessError.correlationId}
            </p>
          )}
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <Button type="button" onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Retry access
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                clearTokens();
                localStorage.removeItem('tenant_id');
                setUser(null);
                setTenant(null);
                window.location.href = '/login';
              }}
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Sign out
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return <>{children}</>;
}
